import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from 'lucide-react';
import { 
  DollarSign, Users, Phone, Mail, Clock, Target, TrendingUp, AlertTriangle,
  CheckCircle, ChevronRight, Sparkles, RefreshCw, Loader2, CalendarDays,
  BarChart3, Flame, ThermometerSun, LayoutDashboard, Flag, Zap, ListTodo
} from 'lucide-react';
import api from '../api';

const SalesWorkspacePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workspace');
  const [loading, setLoading] = useState(true);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [guidedActions, setGuidedActions] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [goalsDashboard, setGoalsDashboard] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    name: '',
    goal_type: 'revenue',
    target_value: '',
    period: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    description: '',
    department_visibility: ['sales'],
    is_shared: true
  });

  useEffect(() => {
    loadWorkspaceData();
  }, []);

  const loadWorkspaceData = async () => {
    try {
      setLoading(true);
      const [wsRes, actionsRes, goalsRes] = await Promise.all([
        api.get('/api/sales/workspace'),
        api.get('/api/sales/workspace/guided-actions?limit=10'),
        api.get('/api/sales/goals')
      ]);
      setWorkspaceData(wsRes.data);
      setGuidedActions(actionsRes.data.actions || []);
      setGoals(goalsRes.data.goals || []);
    } catch (error) {
      console.error('Error loading workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoalsDashboard = async () => {
    try {
      const res = await api.get('/api/sales/goals/dashboard');
      setGoalsDashboard(res.data);
    } catch (error) {
      console.error('Error loading goals dashboard:', error);
    }
  };

  const loadAISuggestions = async () => {
    try {
      setLoadingAI(true);
      const res = await api.post('/api/sales/workspace/ai-suggestions');
      setAiSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error('Error loading AI suggestions:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const completeAction = async (actionId, outcome) => {
    try {
      await api.post(`/api/sales/workspace/guided-actions/${actionId}/complete`, {
        outcome,
        notes: ''
      });
      // Reload actions
      const res = await api.get('/api/sales/workspace/guided-actions?limit=10');
      setGuidedActions(res.data.actions || []);
    } catch (error) {
      console.error('Error completing action:', error);
    }
  };

  const createGoal = async () => {
    try {
      await api.post('/api/sales/goals', {
        ...newGoal,
        target_value: parseFloat(newGoal.target_value) || 0
      });
      setShowGoalDialog(false);
      setNewGoal({
        name: '',
        goal_type: 'revenue',
        target_value: '',
        period: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        description: '',
        department_visibility: ['sales'],
        is_shared: true
      });
      // Reload goals
      const res = await api.get('/api/sales/goals');
      setGoals(res.data.goals || []);
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'call_back': return <Phone className="h-4 w-4" />;
      case 'close_deal': return <DollarSign className="h-4 w-4" />;
      case 'follow_up_lead': return <Users className="h-4 w-4" />;
      case 'follow_up_deal': return <TrendingUp className="h-4 w-4" />;
      case 'review_opportunity': return <AlertTriangle className="h-4 w-4" />;
      case 'send_quote': return <Mail className="h-4 w-4" />;
      default: return <ListTodo className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    if (priority >= 90) return 'bg-red-100 text-red-700';
    if (priority >= 70) return 'bg-orange-100 text-orange-700';
    if (priority >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-blue-100 text-blue-700';
  };

  const getGoalProgress = (goal) => {
    const progress = goal.progress || {};
    return {
      current: progress.current_value || 0,
      target: progress.target_value || goal.target_value || 0,
      percentage: progress.percentage || 0,
      status: progress.status || 'behind'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const ws = workspaceData || {};
  const quickStats = ws.quick_stats || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Workspace</h1>
          <p className="text-slate-500 mt-1">Your unified sales command center</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadWorkspaceData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/sales/deals')}>
            <DollarSign className="h-4 w-4 mr-2" />
            View Deals
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pipeline Value</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${(quickStats.pipeline_value || 0).toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Won This Month</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${(quickStats.won_this_month || 0).toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Today's Meetings</p>
                <p className="text-2xl font-bold text-slate-900">
                  {ws.today?.meeting_count || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <CalendarDays className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Overdue Tasks</p>
                <p className="text-2xl font-bold text-red-600">
                  {ws.today?.overdue_count || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="workspace">
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Workspace
          </TabsTrigger>
          <TabsTrigger value="guided">
            <Zap className="h-4 w-4 mr-2" />
            Guided Actions
          </TabsTrigger>
          <TabsTrigger value="goals" onClick={loadGoalsDashboard}>
            <Target className="h-4 w-4 mr-2" />
            Goals & Targets
          </TabsTrigger>
          <TabsTrigger value="ai" onClick={loadAISuggestions}>
            <Sparkles className="h-4 w-4 mr-2" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Workspace Tab */}
        <TabsContent value="workspace" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Today's Focus */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  Today's Focus
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(ws.today?.meetings || []).length > 0 ? (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Meetings</p>
                    {ws.today.meetings.slice(0, 3).map((meeting, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium text-sm">{meeting.title || 'Meeting'}</p>
                          <p className="text-xs text-slate-500">
                            {meeting.scheduled_at ? new Date(meeting.scheduled_at).toLocaleTimeString() : 'TBD'}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {meeting.activity_type || 'meeting'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No meetings scheduled today</p>
                )}

                {(ws.today?.overdue_tasks || []).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-600 mb-2">Overdue Tasks</p>
                    {ws.today.overdue_tasks.slice(0, 3).map((task, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <p className="text-sm">{task.title}</p>
                        <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hot Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flame className="h-5 w-5 text-orange-500" />
                  Hot Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(ws.hot_leads || []).length > 0 ? (
                  <div className="space-y-2">
                    {ws.hot_leads.map((lead, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {lead.first_name} {lead.last_name}
                          </p>
                          <p className="text-xs text-slate-500">{lead.company_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-orange-100 text-orange-700">
                            Score: {lead.engagement_score || 0}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No hot leads at the moment</p>
                )}
              </CardContent>
            </Card>

            {/* Closing This Week */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-500" />
                  Closing This Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(ws.closing_this_week || []).length > 0 ? (
                  <div className="space-y-2">
                    {ws.closing_this_week.map((deal, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/deals/${deal.id}`)}
                      >
                        <div>
                          <p className="font-medium text-sm">{deal.title}</p>
                          <p className="text-xs text-slate-500">
                            Close: {deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString() : 'TBD'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-emerald-600">
                            ${(deal.value || 0).toLocaleString()}
                          </p>
                          <Badge variant="outline" className="text-xs">{deal.stage}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No deals closing this week</p>
                )}
              </CardContent>
            </Card>

            {/* Engagement Snapshot */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-500" />
                  Engagement Snapshot
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Recently Active Leads</p>
                  {(ws.engagement?.active_leads || []).length > 0 ? (
                    ws.engagement.active_leads.slice(0, 3).map((lead, i) => (
                      <div key={i} className="text-sm py-1 text-slate-600">
                        {lead.first_name} {lead.last_name} - {lead.company_name}
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm">No recent activity</p>
                  )}
                </div>
                
                {(ws.engagement?.stale_opportunities || []).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-amber-600 mb-2">Stale Opportunities</p>
                    {ws.engagement.stale_opportunities.slice(0, 3).map((deal, i) => (
                      <div key={i} className="text-sm py-1 text-slate-600">
                        {deal.title} - ${(deal.value || 0).toLocaleString()}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Guided Actions Tab */}
        <TabsContent value="guided" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                AI-Powered Guided Actions
              </CardTitle>
              <CardDescription>
                Prioritized actions to maximize your sales effectiveness today
              </CardDescription>
            </CardHeader>
            <CardContent>
              {guidedActions.length > 0 ? (
                <div className="space-y-3">
                  {guidedActions.map((action, i) => (
                    <div 
                      key={action.id} 
                      className="flex items-center gap-4 p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getPriorityColor(action.priority)}`}>
                        {getActionIcon(action.type)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{action.title}</p>
                        <p className="text-sm text-slate-500">{action.description}</p>
                        {action.due && (
                          <p className="text-xs text-slate-400 mt-1">
                            Due: {new Date(action.due).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityColor(action.priority)}>
                          Priority: {action.priority}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => completeAction(action.id, 'completed')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>You're all caught up! No pending actions.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Sales Goals & Targets</h2>
            <Button onClick={() => setShowGoalDialog(true)}>
              <Target className="h-4 w-4 mr-2" />
              New Goal
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.map((goal) => {
              const progress = getGoalProgress(goal);
              const progressPercent = Math.min(progress.percentage, 100);
              
              return (
                <Card key={goal.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">{goal.name}</h3>
                        <Badge variant="outline">{goal.period}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{goal.goal_type.replace('_', ' ')}</span>
                        <span className={`font-medium ${
                          progress.status === 'on_track' ? 'text-emerald-600' :
                          progress.status === 'at_risk' ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {progress.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{progress.current.toLocaleString()}</span>
                          <span className="text-slate-500">/ {progress.target.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              progress.status === 'on_track' ? 'bg-emerald-500' :
                              progress.status === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 text-right">
                          {progressPercent.toFixed(1)}% complete
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {goals.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No Goals Set</h3>
                <p className="text-slate-500 mb-4">Create your first sales goal to track your progress</p>
                <Button onClick={() => setShowGoalDialog(true)}>
                  Create Goal
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                AI Sales Suggestions
              </CardTitle>
              <CardDescription>
                Intelligent recommendations based on your CRM data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAI ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                  <span className="ml-2 text-slate-500">Analyzing your data...</span>
                </div>
              ) : aiSuggestions.length > 0 ? (
                <div className="space-y-4">
                  {aiSuggestions.map((suggestion, i) => (
                    <div 
                      key={i}
                      className="p-4 rounded-lg border-l-4 bg-slate-50"
                      style={{
                        borderColor: suggestion.priority === 'high' ? '#ef4444' :
                                   suggestion.priority === 'medium' ? '#f59e0b' : '#3b82f6'
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">{suggestion.title}</h4>
                          <p className="text-sm text-slate-600 mt-1">{suggestion.description}</p>
                          {suggestion.estimated_impact && (
                            <p className="text-xs text-emerald-600 mt-2">
                              Impact: {suggestion.estimated_impact}
                            </p>
                          )}
                        </div>
                        <Badge className={
                          suggestion.priority === 'high' ? 'bg-red-100 text-red-700' :
                          suggestion.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {suggestion.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button onClick={loadAISuggestions}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate AI Suggestions
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Goal Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Sales Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Goal Name</Label>
              <Input
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                placeholder="e.g., Q1 Revenue Target"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Goal Type</Label>
                <Select
                  value={newGoal.goal_type}
                  onValueChange={(v) => setNewGoal({ ...newGoal, goal_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="deals_won">Deals Won</SelectItem>
                    <SelectItem value="deals_created">Deals Created</SelectItem>
                    <SelectItem value="leads_generated">Leads Generated</SelectItem>
                    <SelectItem value="meetings_held">Meetings Held</SelectItem>
                    <SelectItem value="calls_made">Calls Made</SelectItem>
                    <SelectItem value="pipeline_value">Pipeline Value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period</Label>
                <Select
                  value={newGoal.period}
                  onValueChange={(v) => setNewGoal({ ...newGoal, period: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Target Value</Label>
              <Input
                type="number"
                value={newGoal.target_value}
                onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })}
                placeholder="e.g., 100000"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newGoal.start_date}
                  onChange={(e) => setNewGoal({ ...newGoal, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newGoal.end_date}
                  onChange={(e) => setNewGoal({ ...newGoal, end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="Add any notes about this goal..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalDialog(false)}>Cancel</Button>
            <Button onClick={createGoal} disabled={!newGoal.name || !newGoal.target_value}>
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesWorkspacePage;
